import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ShoppingBag, ArrowUpRight, Sparkles, Leaf, Rabbit } from "lucide-react";
import { toast } from "sonner";

const QUICK_CATEGORIES = [
  { slug: "foundation",   label: "Foundation" },
  { slug: "concealer",    label: "Concealer" },
  { slug: "lipstick",     label: "Lipstick" },
  { slug: "mascara",      label: "Mascara" },
  { slug: "primer",       label: "Primer" },
  { slug: "sunscreen",    label: "Sunscreen" },
  { slug: "acne",         label: "Acne care" },
  { slug: "pigmentation", label: "Brightening" },
  { slug: "dark_circles", label: "Under-eye" },
  { slug: "dry_skin",     label: "Moisturizer" },
  { slug: "dandruff",     label: "Anti-dandruff" },
  { slug: "hair_growth",  label: "Hair growth" },
  { slug: "beard_care",   label: "Beard care" },
  { slug: "trimmers",     label: "Trimmers" },
  { slug: "hair_styling", label: "Hair styling" },
];

export default function ShopForYou() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "foundation";
  const [category, setCategory] = useState(initial);
  const [vegan, setVegan] = useState(false);
  const [crueltyFree, setCrueltyFree] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCategory(params.get("category") || "foundation");
  }, [params]);

  useEffect(() => {
    (async () => {
      setLoading(true); setData(null);
      try {
        const { data } = await api.post("/beauty/products", {
          category, vegan, cruelty_free: crueltyFree,
        });
        setData(data);
      } catch (e) {
        toast.error("Couldn't fetch product suggestions.");
      } finally { setLoading(false); }
    })();
  }, [category, vegan, crueltyFree]);

  const pick = (slug) => {
    const next = new URLSearchParams(params);
    next.set("tab", "foryou");
    next.set("category", slug);
    setParams(next, { replace: true });
    setCategory(slug);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="shop-foryou">
      <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Personalised for you</p>
      <h1 className="editorial-h1 mt-3">Shop, tuned to your <em>scan.</em></h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Beauty product suggestions matched to your skin tone, undertone and concerns. Tap any category to refresh.
      </p>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="shop-foryou-categories">
        {QUICK_CATEGORIES.map(c => (
          <button
            key={c.slug}
            type="button"
            data-testid={`shop-foryou-cat-${c.slug}`}
            onClick={() => pick(c.slug)}
            className={`h-9 px-4 text-[11px] uppercase tracking-[0.18em] border transition-colors ${category === c.slug ? "bg-accent text-accent-foreground border-accent" : "border-border hover:border-accent hover:text-accent"}`}
          >{c.label}</button>
        ))}
      </div>

      {/* Ethical filter chips — narrow merchant results server-side */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="shop-foryou-ethics">
        <button
          type="button"
          onClick={() => setVegan((v) => !v)}
          data-testid="shop-foryou-vegan"
          className={`inline-flex items-center gap-1.5 h-9 px-4 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
            vegan ? "bg-accent text-accent-foreground border-accent" : "border-border hover:border-accent hover:text-accent"
          }`}
        >
          <Leaf size={12} /> Vegan
        </button>
        <button
          type="button"
          onClick={() => setCrueltyFree((v) => !v)}
          data-testid="shop-foryou-cruelty-free"
          className={`inline-flex items-center gap-1.5 h-9 px-4 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
            crueltyFree ? "bg-accent text-accent-foreground border-accent" : "border-border hover:border-accent hover:text-accent"
          }`}
        >
          <Rabbit size={12} /> Cruelty-free
        </button>
      </div>

      <div className="mt-10">
        {loading && (
          <div className="border border-border p-16 text-center">
            <Loader2 size={22} className="mx-auto animate-spin" />
          </div>
        )}

        {!loading && data && (
          <div className="border border-border p-6 lg:p-8" data-testid="shop-foryou-result">
            <div>
              <p className="eyebrow">Search intent</p>
              <p className="font-serif text-3xl mt-1 leading-tight">{data.query}</p>
              <p className="text-xs text-muted-foreground mt-2">
                We open the exact search on your preferred marketplace — you shop it, we don&apos;t mark it up.
              </p>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {data.links.map((l) => (
                <a
                  key={l.merchant}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`shop-foryou-link-${l.merchant.toLowerCase()}`}
                  className="group flex items-center justify-between gap-3 border border-border px-4 h-12 hover:border-foreground transition"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <ShoppingBag size={14} /> {l.merchant}
                  </span>
                  <ArrowUpRight size={16} className="opacity-60 group-hover:opacity-100 group-hover:rotate-12 transition-transform" />
                </a>
              ))}
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-6">
              Prefer a specific category? Pick one above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
